import { useMemo } from "react";
import { Button, Icon, Modal, Typography } from "@/components/atoms";
import { DataTable } from "@/components/molecules";
import { DataTableColumnDef } from "@/components/molecules/DataTable/types";
import { GetSubscriptionPlansResponse } from "@/redux/apis/subscription/types";
import { isDisplayableValue, getFeatureDisplayOverride } from "../helpers";

type FeatureValueType = "text" | "available" | "not-included" | "warning";

interface FeatureValue {
  type: FeatureValueType;
  label: string;
}

interface PlanFeatureComparison {
  feature: string;
  currentPlan: FeatureValue;
  newPlan: FeatureValue;
}

interface ReviewPlanChangesModalProps {
  open: boolean;
  onClose: () => void;
  onContinue?: () => void;
  currentPlanId?: string;
  newPlanId?: string;
  subscriptionData?: GetSubscriptionPlansResponse;
}

/**
 * Transforms subscription data into feature comparisons between current and new plan
 *
 * Display logic per CEO/CTO guidance:
 * 1. If a subscription has the feature in its subscriptions array → it's available
 * 2. If defaultValue exists and is displayable (not true/false/-/yes/no) → show as text
 * 3. Otherwise → show "Available" for present, "Not Included" for absent
 */
const buildFeatureComparisons = (
  subscriptionData: GetSubscriptionPlansResponse,
  currentPlanId: string,
  newPlanId: string
): PlanFeatureComparison[] => {
  const comparisons: PlanFeatureComparison[] = [];

  // Sort feature groups by display order and iterate
  const sortedGroups = [...subscriptionData.featureGroups].sort((a, b) => a.displayOrder - b.displayOrder);

  for (const group of sortedGroups) {
    // Sort features by display order
    const sortedFeatures = [...group.features].sort((a, b) => a.displayOrder - b.displayOrder);

    for (const feature of sortedFeatures) {
      // Find feature availability for current and new plan
      // Presence in the subscriptions array means the feature is available
      const currentPlanFeature = feature.subscriptions.find((sub) => sub.subscriptionId === currentPlanId);
      const newPlanFeature = feature.subscriptions.find((sub) => sub.subscriptionId === newPlanId);

      // Determine feature values based on presence, displayable value, and display overrides
      const getFeatureValue = (planFeature: typeof currentPlanFeature, planId: string): FeatureValue => {
        if (!planFeature) {
          // Feature not associated with this subscription = not included
          return { type: "not-included", label: "Not Included" };
        }
        // Check for display override first (marketing/pricing text)
        const override = getFeatureDisplayOverride(feature.featureName, planId);
        if (override) {
          if (override.type === "text") {
            return { type: "text", label: override.content.replace(/\n/g, " ") };
          }
          if (override.type === "badge") {
            return { type: "text", label: override.label };
          }
        }
        // Feature is associated with this subscription = available
        if (planFeature.defaultValue && isDisplayableValue(planFeature.defaultValue)) {
          // Has a meaningful display value (number, "Unlimited", etc.)
          return { type: "text", label: planFeature.defaultValue };
        }
        // Feature is simply available (no special value to display)
        return { type: "available", label: "Available" };
      };

      const currentValue = getFeatureValue(currentPlanFeature, currentPlanId);
      const newValue = getFeatureValue(newPlanFeature, newPlanId);

      // Only include features where there's a difference (downgrade impact)
      if (currentValue.type !== newValue.type || currentValue.label !== newValue.label) {
        comparisons.push({
          feature: feature.featureName,
          currentPlan: currentValue,
          newPlan: newValue,
        });
      }
    }
  }

  return comparisons;
};

const FeatureValueCell = ({ value }: { value: FeatureValue }) => {
  switch (value.type) {
    case "available":
      return (
        <div className="flex flex-row items-center gap-2">
          <Icon name="TickCircle" variant="Bold" size="md" className="flex-shrink-0 text-success" />
          <Typography variant="paragraph">{value.label}</Typography>
        </div>
      );
    case "not-included":
      return (
        <div className="flex flex-row items-center gap-2">
          <Icon name="CloseCircle" variant="Bold" size="md" className="flex-shrink-0 text-danger" />
          <Typography variant="paragraph">{value.label}</Typography>
        </div>
      );
    case "warning":
      return (
        <div className="flex flex-row items-center gap-2">
          <Icon name="Danger" variant="Bold" size="md" className="flex-shrink-0 text-warning" />
          <Typography variant="paragraph">{value.label}</Typography>
        </div>
      );
    case "text":
    default:
      return <Typography variant="paragraph">{value.label}</Typography>;
  }
};

export const ReviewPlanChangesModal = ({
  open,
  onClose,
  onContinue,
  currentPlanId,
  newPlanId,
  subscriptionData,
}: ReviewPlanChangesModalProps) => {
  const handleCancel = () => {
    onClose();
  };

  const handleContinue = () => {
    onContinue?.();
  };

  // Build feature comparisons from subscription data
  const features = useMemo(() => {
    if (!subscriptionData || !currentPlanId || !newPlanId) {
      return [];
    }
    return buildFeatureComparisons(subscriptionData, currentPlanId, newPlanId);
  }, [subscriptionData, currentPlanId, newPlanId]);

  const columns = useMemo<DataTableColumnDef<PlanFeatureComparison>[]>(
    () => [
      {
        accessorKey: "feature",
        header: "Features",
        cell: (props) => (
          <Typography variant="paragraph" className="text-neutral-dark-gray">
            {props.row.original.feature}
          </Typography>
        ),
        enableSorting: false,
        size: 33,
      },
      {
        accessorKey: "currentPlan",
        header: "Current Plan",
        cell: (props) => <FeatureValueCell value={props.row.original.currentPlan} />,
        enableSorting: false,
        size: 33,
      },
      {
        accessorKey: "newPlan",
        header: "New Plan",
        cell: (props) => <FeatureValueCell value={props.row.original.newPlan} />,
        enableSorting: false,
        size: 33,
      },
    ],
    []
  );

  return (
    <Modal open={open} onClose={onClose} className="w-[85vw] md:w-[680px] lg:w-[680px]">
      <div className="flex flex-col gap-6">
        {/* Title and Description */}
        <div className="flex flex-col gap-2 text-neutral-dark-gray">
          <Typography variant="title">Review your plan changes</Typography>
          <Typography variant="paragraph">
            Before confirming your downgrade, please review the features and access that will change with your new plan.
          </Typography>
        </div>

        {/* Comparison Table */}
        <DataTable
          columns={columns}
          data={features}
          className="max-h-[400px] overflow-y-auto [&_thead]:sticky [&_thead]:top-0 [&_thead]:z-10"
        />

        {/* Action Buttons */}
        <div className="flex flex-row items-center justify-end gap-3">
          <Button type="button" size="lg" variant="secondary" onClick={handleCancel}>
            Cancel
          </Button>
          <Button type="button" size="lg" variant="primary" onClick={handleContinue}>
            Continue
          </Button>
        </div>
      </div>
    </Modal>
  );
};
